import { baseApi } from '@/lib/utils'

export interface CreateLeaveRequestPayload {
  employee_id?: number // HR override only — self-service omits this
  leave_type: string
  from_date: string
  to_date: string
  from_half?: 'full' | 'first' | 'second'
  to_half?: 'full' | 'first' | 'second'
  reason?: string
  notify?: number[]
  is_emergency?: boolean // HR-only — bypasses the notice-period check
}

export interface CreateLeaveRequestResponse {
  success: boolean
  message: string
  data: {
    task_id: string
    total_leave_days: number
    converted_to_unpaid: boolean
    approver: {
      clickupId: number | null
      name: string | null
      source: string | null
    }
  }
}

export interface LeaveReportItem {
  id: number
  task_id: string
  employee_id: number
  user_name: string
  profile_picture: string | null
  team_id: number | null
  team: string | null
  leave_type: string
  leave_duration: string
  from_date: string
  to_date: string
  total_leave_days: number
  reason: string | null
  department: string | null
  status: string
  created_at: string
}

export interface GetLeaveReportParams {
  employee_id?: number
  search?: string
  status?: string
  leave_type?: string
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
  sort_by?: string
  order?: 'ASC' | 'DESC'
}

export interface LeaveReportResponse {
  success: boolean
  data: LeaveReportItem[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export const leaveRequestApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Keka-style "Request Leave" form submits here — see
    // controller/leaveRequest.controller.js#createLeaveRequest on the backend.
    createLeaveRequest: builder.mutation<
      CreateLeaveRequestResponse,
      CreateLeaveRequestPayload
    >({
      query: payload => ({
        url: '/leaves/request',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['LeaveRequests', 'LeaveBalances'],
    }),

    // Optional follow-up after createLeaveRequest — a separate call (not
    // folded into the JSON create request) so a slow/failed file upload
    // can't block the leave request itself from going through.
    uploadLeaveAttachment: builder.mutation<
      { success: boolean; message: string },
      { taskId: string; file: File }
    >({
      query: ({ taskId, file }) => {
        const formData = new FormData()
        formData.append('attachment', file)
        return {
          url: `/leaves/request/${taskId}/attachment`,
          method: 'POST',
          body: formData,
        }
      },
    }),

    getLeaveReport: builder.query<LeaveReportResponse, GetLeaveReportParams>({
      query: (params = {}) => ({
        url: '/leaves/report',
        params,
      }),
      providesTags: ['LeaveRequests'],
    }),

    emailLeaveReport: builder.mutation<
      { success: boolean; message: string },
      { employee_id?: number }
    >({
      query: (body = {}) => ({
        url: '/leaves/report/email',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useCreateLeaveRequestMutation,
  useUploadLeaveAttachmentMutation,
  useGetLeaveReportQuery,
  useEmailLeaveReportMutation,
} = leaveRequestApi
